import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {NavigationEnd, Router} from "@angular/router";
import * as fromStore from "../../../../../store";
import {DbStoreState} from "../../../../../store";
import {Store} from '@ngrx/store';
import {RouteDataService} from "../../../../../shared/services/route-data/route-data.service";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {distinctUntilChanged, filter, first, switchMap, takeUntil} from "rxjs/operators";
import {User} from "../../../../../user/user";
import {combineLatest, Observable, Subject} from "rxjs";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {DbValidators} from "../../../../../shared/validators/DbValidators";
import {DbResponse} from "../../../../../shared/interceptors/db-response";
import {HostingService} from "../../../../../websites/website/hosting/hosting.service";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {IActivity} from "../../../../../activities/activity";
import {Website} from "../../../../../websites/website/website";
import {AuthService} from "../../../../../auth/auth.service";
import {TypeFormAnswers} from "../../../../../add-website/typeFormAnswers";
import {GlobalEvent} from "../../../../../shared/services/global-event-dispatcher/global-event";
import {
  GlobalEventDispatcherService
} from "../../../../../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import * as userActions from "../../../../../store/actions/user.action";
import {UserSubscription, UserSubscriptionCategory} from "../../../../../user/user-subscription";
import {Utils} from "../../../../../shared/utils/utils";
import {UserService} from "../../../../../user/user.service";
import {IDsDropdownOption} from "../../../../../shared/components/ds-dropdown/ds-dropdown-option";

type createAIFinalizeStepStatus = 'todo' | 'process' | 'passed';

interface RegionDropdownOption extends IDsDropdownOption {
  isAvailable?: boolean;
}

interface ICreateWebsiteFinalizeStep {
  label: string;
  status: createAIFinalizeStepStatus | string;
}

@Component({
    selector: 'db-create-options',
    templateUrl: './create-options.component.html',
    styleUrls: ['./create-options.component.scss'],
    standalone: false
})
export class CreateOptionsComponent implements OnInit, OnDestroy {
  @Input() showTitle?: boolean = true;

  public pageId;
  public user: User;
  public userSubscription: UserSubscription;
  public headerTitle: string;
  public pageIdFromUrl: string;
  public websiteCreateForm: FormGroup;
  public loadingContent: boolean;
  public loading: boolean;
  public hasServerFieldErrors: boolean;
  public serverErrorsMessage: string;
  public step3: string;
  public ongoingActivity$: Observable<IActivity>;
  public typeFormAnswers = TypeFormAnswers;
  public isMobile: boolean = window.innerWidth <= 991;

  public region: FormControl;
  public filteredWebsites: Website[];
  public regionOptions: RegionDropdownOption[];
  public domainId: any;
  public showDomainInput: string = '';
  public desiredDomainName: string = '';
  public userLocalStorage: any;
  public steps: ICreateWebsiteFinalizeStep[];
  public companyName: string = 'Dashboard';

  protected readonly UserSubscriptionCategory = UserSubscriptionCategory;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  @Output() buttonClicked = new EventEmitter<any>();

  constructor(
    private _store: Store<DbStoreState>,
    private _router: Router,
    private _routeDataService: RouteDataService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _hostingService: HostingService,
    private _authService: AuthService,
    private _globalEventDispatcher: GlobalEventDispatcherService,
    private _userService: UserService,
  ) {
  }

  ngOnInit() {
    this._getCompanyInfo();
    this.steps = [
      {
        "label": `Creating a blank website on ${this.companyName} Hosting`,
        "status": "process"
      },
      {
        "label": "Creating Home page",
        "status": "todo"
      },
      {
        "label": "Creating Services, Contact, About pages",
        "status": "todo",
      }
    ];
    this.pageIdFromUrl = this._router.url.split("/").pop().toLowerCase();
    this.ongoingActivity$ = this._store.select(fromStore.getCreateWebsiteOngoingActivity);
    this.region = new FormControl("");
    this.regionOptions = [];
    this.websiteCreateForm = new FormGroup({
      'subdomain': new FormControl("", [DbValidators.trim, Validators.required, Validators.pattern('^([a-z0-9]+(-[a-z0-9]+)*)$')]),
      'region': this.region,
      'siteTitle': new FormControl("", [DbValidators.trimStart, Validators.required])
    });
    this.pageId = this._routeDataService.getCurrentRouteData().pageId;
    this.loadingContent = true;
    combineLatest(
      this._store.select(fromStore.getWebsitesLoaded),
      this._store.select(fromStore.getUserLoaded)
    )
      .pipe(
        filter(([userLoaded, websitesLoaded]) => userLoaded && websitesLoaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getWebsites),
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription),
          this._store.select(fromStore.getUserLocalStorage),
        )),
        filter(([websites, user]) => !!user && !!websites),
        distinctUntilChanged(([websitesOld],
                              [websitesNew]) => websitesOld && websitesNew && websitesOld.length === websitesNew.length)
      )
      .subscribe(([websites, user, userSubscription, userLocalStorage]) => {
        this.user = user;
        this.userSubscription = userSubscription;
        this.userLocalStorage = userLocalStorage;
        this.filteredWebsites = websites.filter(website => website.type === 'live');
        this.getOngoingActivities();
        this.getRegions();
      });

    if (!this.websiteCreateForm.controls['subdomain'].value) {
      this.getSubdomainSuggestion();
    }
    this.headerTitle = this.getHeaderTitle();
    this.routerEvents();
  }

  public getHeaderTitle() {
    const replaceString = {
      '-': " ",
      'and': "&",
    };
    const re = new RegExp(Object.keys(replaceString).join("|"), "gi");
    let headerTitle;
    const pageTitle = (this._router && this._router.url.split("/").pop().replace(re, (matched) => {
      return replaceString[matched];
    }));
    headerTitle = 'Create a website with <span>' +
      (pageTitle === 'install blank' ? 'Your Theme & Plugins' : pageTitle) + '</span>';
    return headerTitle;
  }

  public routerEvents(): void {
    this._router.events
      .pipe(filter((event: any) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.pageId = this._router && this._router.url.split("/").pop();
      });
  }

  public getOngoingActivities() {
    this.ongoingActivity$
      .pipe(
        takeUntil(this._destroyStream$),
        filter(activity => !!activity)
      )
      .subscribe((activity) => {
        if (activity && activity.status === 'running') {
          this.loading = true;
        } else if ((!activity || (activity && activity.status === 'done'))) {
          this._store.dispatch(new fromStore.LoadWebsites());
          // this._store.dispatch(new fromStore.LoadUser());
          let filteredWebsites: any[];
          filteredWebsites = this.filteredWebsites &&
            this.filteredWebsites.filter((website: Website) => website.type === 'live');

          if (TypeUtils.isDefined(filteredWebsites) && filteredWebsites.length) {
            this.loading = true;
            this.navigateToWebsite(filteredWebsites[0].id);
          }
        }

        if (!activity && this.loading && !this.loadingContent && this.filteredWebsites.length) {
          this.step3 = 'done';
          this.navigateToWebsite(this.filteredWebsites[0].id);
        }
      });
  }

  public getRegions() {
    this._hostingService.regions(this.user.subscriptionId).pipe(
      filter((response: DbResponse) => !!response),
      first())
      .subscribe((response: DbResponse) => {
        if (response.success) {
          this.regionOptions = response.data;
          const regionsFormatted = [];
          Object.keys(this.regionOptions).forEach((key: string): void => {
            regionsFormatted.push({
              label: this.regionOptions[key].location,
              value: this.regionOptions[key].zone,
              isAvailable: this.regionOptions[key].is_available
            });
          });
          this.regionOptions = regionsFormatted;
          if (this.regionOptions && this.regionOptions.length) {
            const region = this.regionOptions.find(_region => _region.isAvailable);
            this.region.setValue(region && region.value);
          }
          if (this.websiteCreateForm.controls['subdomain'].value !== "") {
            this.loadingContent = false;
          }
        }
      });
  }

  public getSubdomainSuggestion() {
    this._hostingService.subdomainSuggestion()
      .pipe(filter((response: DbResponse) => !!response),
        first())
      .subscribe((response: DbResponse) => {
        if (response.success) {
          this.websiteCreateForm.controls['subdomain'].setValue(response.data.subdomain.toLowerCase());
          if (this.websiteCreateForm.controls['region'].value !== "") {
            this.loadingContent = false;
          }
        }
      });
  }

  public submit() {
    if (this.websiteCreateForm.valid) {
      if (this.websiteCreateForm.controls['siteTitle'].value === '') {
        this.websiteCreateForm.controls['siteTitle'].setValue('My Website');
      }
      this.createHostedWebsiteForTrialUsers(this.websiteCreateForm.value);
    }
  }

  public createHostedWebsiteForTrialUsers(websiteData): void {
    this.loading = true;
    this.buttonClicked.emit(true);
    this._globalEventDispatcher.dispatchEvent(GlobalEvent.hideOnboardingBackButton, 1);
    const demoUser = Utils.isDemoUser(this.userSubscription, this.user);
    this._hostingService.createWebsiteForTrialUsers(websiteData, demoUser)
      .pipe(filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response: DbResponse): void => {
        setTimeout(() => {
          if (this.user && this.user.signUpFor) {
            this.user.signUpFor['Action'] = {answer: 'create-website', id: ''};
            this._authService.updateSignUpFor(this.user.signUpFor, true)
              .pipe(filter((_response: DbResponse): boolean => !!_response),
                first())
              .subscribe((_response) => {
                this._store.dispatch(new fromStore.LoadUser());
                if (response.success && response.data && response.data.domain_id) {
                  this.step3 = 'done';
                  this.navigateToWebsite(response.data.domain_id);
                } else {
                  this.loadingContent = false;
                  this.loading = false;
                  this.hasServerFieldErrors = !TypeUtils.isEmpty(response?.serverErrors?.fields);
                  this.serverErrorsMessage = response.message;
                }
              });
          } else {
            this._store.dispatch(new fromStore.LoadUser());
            if (response.success && response.data && response.data.domain_id) {
              this.step3 = 'done';
              this.navigateToWebsite(response.data.domain_id);
            } else {
              this.loadingContent = false;
              this.loading = false;
              this.hasServerFieldErrors = !TypeUtils.isEmpty(response.serverErrors.fields);
              this.serverErrorsMessage = response.message;
            }
          }
        }, 1000);
      });
  }

  public domainShow(answer): void {
    this.showDomainInput = answer;
  }

  public onDesiredDomainNameChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.desiredDomainName = target.value;
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private navigateToWebsite(domainId) {
    this.domainId = domainId;
    this._store.dispatch(new fromStore.LoadWebsites());
    this._store.dispatch(new fromStore.LoadUser());
    this._store.dispatch(new userActions.LoadUserSubscription());
    switch (this.pageIdFromUrl) {
      case 'ai-builder':
      case 'convert-wp':
        if (this.user && this.user.signUpFor) {
          this.user.signUpFor['CreationOption'] = {
            answer: this.pageIdFromUrl,
            id: this.typeFormAnswers.BUILD_WITH_TENWEB_IO
          };
          this._authService.updateSignUpFor(this.user.signUpFor, true);
        }
        if (this.pageIdFromUrl === 'convert-wp') {
          const recreateAnalyticsData = {
            eventCategory: 'Onboarding',
            eventAction: 'Change Hosting',
            eventLabel: 'Convert to WP - blank'
          };
          this._googleAnalyticsService.sendAnalyticsEventWithCore(recreateAnalyticsData);
        }
        this._router.navigate([`/websites/${domainId}/onboarding/onboarding-recreate-website`]);
        break;
      case 'templates':
        if (this.user && this.user.signUpFor) {
          this.user.signUpFor['CreationOption'] = {
            answer: this.pageIdFromUrl,
            id: this.typeFormAnswers.DESIGNER_MADE_TEMPLATE
          };
          this._authService.updateSignUpFor(this.user.signUpFor, true);
        }
        this._router.navigate([`/websites/${domainId}/onboarding/onboarding-templates`]);
        break;
      case 'install-blank':
        if (this.user && this.user.signUpFor) {
          this.user.signUpFor['CreationOption'] = {
            answer: this.pageIdFromUrl,
            id: this.typeFormAnswers.USING_YOUR_PLUGINS
          };
          this._authService.updateSignUpFor(this.user.signUpFor, true);
        }
        const analyticsData = {
          eventCategory: 'Onboarding',
          eventAction: 'Create a Website',
          eventLabel: 'Own Theme - View'
        };
        this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
        this._router.navigate(['/websites'], {
          queryParams: {
            hostedTrialStarted: 'blank'
          }
        });
        break;
    }
  }

  private _getCompanyInfo(): void {
    this._store.select(fromStore.getCompanyInfoLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getCompanyInfo)),
        first()
      )
      .subscribe((companyInfo: any) => {
        this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
        // Update the first step label with the company name
        if (this.steps && this.steps.length > 0) {
          this.steps[0].label = `Creating a blank website on ${this.companyName} Hosting`;
        }
      });
  }

}
